import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowCounterClockwise,
  CheckCircle,
  FileCsv,
  MapPin,
  Sparkle,
  Warning,
} from '@phosphor-icons/react';

import {
  geocodeAutocomplete,
  geocodePlaceDetails,
  newGeocodeSessionToken,
  type GeocodePlace,
} from '../api/geocode';
import {
  matchLocationColumns,
  type LocationImportField,
  type LocationImportValidationError,
} from '../api/locations';
import {
  FIELDS,
  applyLayout,
  canImport,
  defaultLayouts,
  defaultZoneNamesForSplit,
  extractRawSheets,
  previewSheets,
  projectParsedRow,
  suggestionsToMapping,
  type LocationFieldMapping,
  type ResolvedLocationRow,
  type SplitOption,
} from '../lib/locationImport';
import { LogoLoader } from './LogoLoader';
import { clientLog } from '../lib/clientLog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// LocationImportFlow renders the full import UX: file picker → AI column
// mapping → Google Places resolution per row → preview list. State lives on
// the parent so the onboarding wizard can persist progress across step
// navigation and the Locations page can mount it inside a dialog.
//
// The component does NOT submit to the server itself. The parent owns
// "Import now" (Locations page) or "wait until the wizard's Slutför fires"
// (onboarding).

export interface LocationImportState {
  fileName: string;
  parsedRows: Record<string, string>[];
  parsedHeaders: string[];
  parsedSheetNames: string[];
  mapping: LocationFieldMapping;
  suggestionSource: 'ai' | 'fuzzy' | null;
  // Per-row resolution status. Stays in lockstep with resolvedRows by index.
  // 'pending' = mapping done, not yet attempted; 'resolving' = call in
  // flight; 'resolved' = Places hit; 'not_found' = Places miss (row still
  // importable as name-only); 'manual' = the user has marked the row as
  // name-only without consulting Places.
  resolutionStatus: ResolutionStatus[];
  // Index-aligned with parsedRows post-projection. Each row carries name +
  // notes + color + (after resolution) the Places fields.
  resolvedRows: ResolvedLocationRow[];
  validationErrors: LocationImportValidationError[];
  // Default zone split applied to every imported venue. 1 = no zones,
  // 2 = halves (A/B), 4 = quarters (1A/1B/2A/2B), 8 = eighths (1A..2D).
  // Defaults to 8 — the user can change it before committing or edit per-
  // location zones via the edit modal post-import.
  splitOption: SplitOption;
}

export type ResolutionStatus =
  | 'pending'
  | 'resolving'
  | 'resolved'
  | 'not_found'
  | 'manual';

export const emptyLocationImportState = (): LocationImportState => ({
  fileName: '',
  parsedRows: [],
  parsedHeaders: [],
  parsedSheetNames: [],
  mapping: {},
  suggestionSource: null,
  resolutionStatus: [],
  resolvedRows: [],
  validationErrors: [],
  splitOption: 8,
});

export const locationImportHasFile = (s: LocationImportState): boolean =>
  s.parsedRows.length > 0 && s.parsedHeaders.length > 0;

export const locationImportIsReady = (s: LocationImportState): boolean => {
  if (s.parsedRows.length === 0) return false;
  if (!canImport(s.mapping)) return false;
  if (s.validationErrors.length > 0) return false;
  // Every row's resolution status must be terminal (not 'pending' / 'resolving').
  return s.resolutionStatus.every(
    (st) => st === 'resolved' || st === 'not_found' || st === 'manual',
  );
};

interface Props {
  token: string | null;
  language: string;
  region: string;
  state: LocationImportState;
  onChange: (patch: Partial<LocationImportState>) => void;
}

// Bound on concurrent geocode requests. Google's per-key QPS budget is
// generous (~50/s) but we share it with autocomplete in other parts of the
// UI; 6 in flight gives ~30 calls/s for a 50-row import which lands in 3-4
// seconds without coming close to the limit.
const GEOCODE_CONCURRENCY = 6;

export function LocationImportFlow({ token, language, region, state, onChange }: Props) {
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const hasFile = locationImportHasFile(state);

  const onFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setErrorCode(null);
    try {
      const rawSheets = await extractRawSheets(file);
      if (rawSheets.length === 0 || rawSheets.every((s) => s.rows.length === 0)) {
        setErrorCode('empty_file');
        return;
      }
      const result = await matchLocationColumns(token, previewSheets(rawSheets));
      const layouts = result.layouts && result.layouts.length > 0 ? result.layouts : defaultLayouts(rawSheets);
      const out = applyLayout(rawSheets, layouts);
      if (out.headers.length === 0 || out.rows.length === 0) {
        setErrorCode('empty_file');
        return;
      }
      const mapping = suggestionsToMapping(result.suggestions);
      onChange({
        fileName: file.name,
        parsedRows: out.rows,
        parsedHeaders: out.headers,
        parsedSheetNames: out.sheetNames,
        mapping,
        suggestionSource: result.source,
        resolutionStatus: out.rows.map(() => 'pending'),
        resolvedRows: out.rows.map((r) => projectParsedRow(r, mapping)),
        validationErrors: [],
      });
    } catch (err) {
      clientLog.error('locations: import file parse / AI mapping failed', {
        error: err,
        fields: {
          step: 'locations/import',
          stage: 'parse_or_match',
        },
      });
      setErrorCode('parse_error');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setErrorCode(null);
    onChange(emptyLocationImportState());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // When the user re-picks a column mapping, re-project the parsed rows so
  // the name / notes / color / rawAddress lookup reflects the new mapping.
  // Resolution status is also reset — addresses already fetched for the
  // PREVIOUS mapping aren't useful for the new one.
  const applyMappingChange = (field: LocationImportField, column: string) => {
    const next = { ...state.mapping };
    if (column === '') delete next[field];
    else next[field] = column;
    const reprojected = state.parsedRows.map((r) => projectParsedRow(r, next));
    onChange({
      mapping: next,
      resolvedRows: reprojected,
      resolutionStatus: reprojected.map(() => 'pending'),
      validationErrors: [],
    });
  };

  // Kick off geocode resolution whenever the mapping becomes valid and we
  // still have pending rows. Single batched update at the end avoids
  // trampling concurrent worker results against a stale base snapshot.
  const [resolving, setResolving] = useState(false);
  useEffect(() => {
    if (!canImport(state.mapping)) return;
    const hasPending = state.resolutionStatus.some((s) => s === 'pending');
    if (!hasPending) return;
    let cancelled = false;
    setResolving(true);
    void resolveAll({
      token,
      language,
      region,
      rows: state.resolvedRows,
      status: state.resolutionStatus,
    }).then((result) => {
      if (cancelled) return;
      onChange({
        resolvedRows: result.rows,
        resolutionStatus: result.status,
      });
      setResolving(false);
    });
    return () => {
      cancelled = true;
      setResolving(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.mapping.address, state.mapping.name, state.parsedRows.length]);

  if (loading) {
    return (
      <div
        role="status"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          padding: '2rem 1rem',
        }}
      >
        <LogoLoader label={t('locations.import.aiMapping')} size={220} />
        <p style={{ color: 'var(--color-fg-muted)', margin: 0 }}>
          {t('locations.import.aiMapping')}
        </p>
      </div>
    );
  }

  if (hasFile) {
    return (
      <div className="form" style={{ gap: '1rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <p style={{ margin: 0 }}>
            {t('locations.import.fileSummary', {
              count: state.parsedRows.length,
              fileName: state.fileName,
            })}
          </p>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              reset();
              setTimeout(() => fileInputRef.current?.click(), 0);
            }}
          >
            <ArrowCounterClockwise size={16} aria-hidden />
            {t('locations.import.changeFile')}
          </button>
        </div>

        {state.suggestionSource === 'ai' && (
          <p
            className="form__hint"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <Sparkle size={16} aria-hidden weight="fill" style={{ flexShrink: 0 }} />
            <span>{t('locations.import.aiAssisted')}</span>
          </p>
        )}

        <MappingTable
          headers={state.parsedHeaders}
          rows={state.parsedRows}
          mapping={state.mapping}
          onChange={applyMappingChange}
        />

        {canImport(state.mapping) && (
          <SplitSelector
            value={state.splitOption}
            onChange={(v) => onChange({ splitOption: v })}
          />
        )}

        {canImport(state.mapping) && (
          <ResolutionPreview
            resolvedRows={state.resolvedRows}
            status={state.resolutionStatus}
            resolving={resolving}
            onMarkManual={(idx) => {
              const current = state.resolvedRows[idx]!;
              // Drop the Places fields so the row submits as name-only. Build a
              // fresh object instead of spreading + setting undefined — with
              // exactOptionalPropertyTypes, undefined is not assignable to the
              // optional string fields.
              const stripped: ResolvedLocationRow = { name: current.name };
              if (current.color) stripped.color = current.color;
              if (current.notes) stripped.notes = current.notes;
              if (current.rawAddress) stripped.rawAddress = current.rawAddress;
              onChange({
                resolutionStatus: replaceAt(state.resolutionStatus, idx, 'manual'),
                resolvedRows: replaceAt(state.resolvedRows, idx, stripped),
              });
            }}
          />
        )}

        {!canImport(state.mapping) && (
          <p
            role="alert"
            className="error"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Warning size={16} aria-hidden style={{ flexShrink: 0 }} />
            <span>{t('locations.import.error.name_required')}</span>
          </p>
        )}

        {state.validationErrors.length > 0 && (
          <ValidationErrorList errors={state.validationErrors} />
        )}
      </div>
    );
  }

  return (
    <div className="form">
      <p>{t('locations.import.body')}</p>

      <button
        type="button"
        className="btn btn--primary"
        onClick={() => fileInputRef.current?.click()}
        style={{ alignSelf: 'flex-start' }}
      >
        <FileCsv size={16} aria-hidden />
        {t('locations.import.pickFile')}
      </button>

      <p className="form__hint">{t('locations.import.formats')}</p>

      {errorCode && (
        <p role="alert" className="error">
          {t(`locations.import.error.${errorCode}`, {
            defaultValue: t('locations.import.error.unknown'),
          })}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        onChange={onFilePicked}
        style={{ display: 'none' }}
      />
    </div>
  );
}

function MappingTable({
  headers,
  rows,
  mapping,
  onChange,
}: {
  headers: string[];
  rows: Record<string, string>[];
  mapping: LocationFieldMapping;
  onChange: (field: LocationImportField, column: string) => void;
}) {
  const { t } = useTranslation('common');

  const firstSampleByColumn = useMemo(() => {
    const out: Record<string, string> = {};
    for (const h of headers) {
      let sample = '';
      for (const r of rows) {
        const v = (r[h] ?? '').trim();
        if (v) {
          sample = v;
          break;
        }
      }
      out[h] = sample || '—';
    }
    return out;
  }, [headers, rows]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '0.5rem', width: '12rem' }}>
              {t('locations.import.column.field')}
            </th>
            <th style={{ textAlign: 'left', padding: '0.5rem' }}>
              {t('locations.import.column.sourceColumn')}
            </th>
            <th style={{ textAlign: 'left', padding: '0.5rem' }}>
              {t('locations.import.column.preview')}
            </th>
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((f) => {
            const picked = mapping[f.value] ?? '';
            return (
              <tr key={f.value}>
                <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                  {t(`locations.import.field.${f.value}`)}
                  {f.required && (
                    <span
                      aria-hidden
                      style={{ color: 'var(--color-error)', marginLeft: '0.25rem' }}
                    >
                      *
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <Select
                    value={picked === '' ? '__ignore__' : picked}
                    onValueChange={(v) =>
                      onChange(f.value, v === '__ignore__' ? '' : v)
                    }
                  >
                    <SelectTrigger aria-label={t(`locations.import.field.${f.value}`)}>
                      <SelectValue />
                    </SelectTrigger>
                    {/* z-[70] keeps the popover above the LocationImportDialog
                        overlay (z-[60]) when the import flow is rendered inside
                        the dialog. Default SelectContent z-50 puts the dropdown
                        BEHIND the overlay so clicks hit the overlay and dismiss. */}
                    <SelectContent className="z-[70]">
                      <SelectItem value="__ignore__">
                        {t('locations.import.field.ignore')}
                      </SelectItem>
                      {headers.map((h) => (
                        <SelectItem key={h} value={h}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td style={{ padding: '0.5rem', color: 'var(--color-fg-muted)' }}>
                  {picked ? (firstSampleByColumn[picked] ?? '—') : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SplitSelector({
  value,
  onChange,
}: {
  value: SplitOption;
  onChange: (v: SplitOption) => void;
}) {
  const { t } = useTranslation('common');
  // Preview of the zone names this split will produce — shown next to the
  // dropdown so the user sees what they're committing to before importing.
  // Empty preview for 1 ("whole place, no zones") so the row reads as
  // "No zones" instead of an empty list.
  const names = defaultZoneNamesForSplit(value);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <label
        htmlFor="location-import-split"
        style={{ fontWeight: 600, fontSize: '0.875rem' }}
      >
        {t('locations.import.split.label')}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <Select
          value={String(value)}
          onValueChange={(v) => onChange(Number(v) as SplitOption)}
        >
          <SelectTrigger
            id="location-import-split"
            aria-label={t('locations.import.split.label')}
            style={{ maxWidth: '12rem' }}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[70]">
            <SelectItem value="1">{t('locations.import.split.option.1')}</SelectItem>
            <SelectItem value="2">{t('locations.import.split.option.2')}</SelectItem>
            <SelectItem value="4">{t('locations.import.split.option.4')}</SelectItem>
            <SelectItem value="8">{t('locations.import.split.option.8')}</SelectItem>
          </SelectContent>
        </Select>
        <span style={{ color: 'var(--color-fg-muted)', fontSize: '0.875rem' }}>
          {names.length === 0
            ? t('locations.import.split.previewNone')
            : t('locations.import.split.preview', { names: names.join(', ') })}
        </span>
      </div>
    </div>
  );
}

function ResolutionPreview({
  resolvedRows,
  status,
  resolving,
  onMarkManual,
}: {
  resolvedRows: ResolvedLocationRow[];
  status: ResolutionStatus[];
  resolving: boolean;
  onMarkManual: (idx: number) => void;
}) {
  const { t } = useTranslation('common');

  const counts = useMemo(() => {
    let resolved = 0;
    let notFound = 0;
    let pending = 0;
    for (const s of status) {
      if (s === 'resolved') resolved++;
      else if (s === 'not_found') notFound++;
      else if (s === 'pending' || s === 'resolving') pending++;
    }
    return { resolved, notFound, pending };
  }, [status]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <p style={{ margin: 0, fontWeight: 600 }}>
        {t('locations.import.resolution.title', { count: resolvedRows.length })}
      </p>
      <p className="form__hint" style={{ margin: 0 }}>
        {resolving
          ? t('locations.import.resolution.resolving')
          : t('locations.import.resolution.summary', {
              resolved: counts.resolved,
              notFound: counts.notFound,
              pending: counts.pending,
            })}
      </p>

      <div style={{ overflowX: 'auto', maxHeight: '20rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.5rem', width: '2rem' }} />
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>
                {t('locations.import.resolution.column.name')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>
                {t('locations.import.resolution.column.address')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem', width: '8rem' }} />
            </tr>
          </thead>
          <tbody>
            {resolvedRows.map((row, idx) => {
              const st = status[idx] ?? 'pending';
              return (
                <tr key={idx}>
                  <td style={{ padding: '0.5rem' }}>{renderStatusIcon(st)}</td>
                  <td style={{ padding: '0.5rem' }}>{row.name || <em>—</em>}</td>
                  <td style={{ padding: '0.5rem', color: 'var(--color-fg-muted)' }}>
                    {row.formatted_address || row.rawAddress || <em>—</em>}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {st === 'not_found' && row.rawAddress && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onMarkManual(idx)}
                      >
                        {t('locations.import.resolution.useAsIs')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderStatusIcon(st: ResolutionStatus) {
  switch (st) {
    case 'resolved':
      return <CheckCircle size={16} weight="fill" aria-label="Resolved" style={{ color: 'var(--color-success, #2a7d4f)' }} />;
    case 'manual':
      return <MapPin size={16} weight="duotone" aria-label="Manual entry" />;
    case 'not_found':
      return <Warning size={16} weight="fill" aria-label="Not found" style={{ color: 'var(--color-warning, #d97706)' }} />;
    case 'resolving':
    case 'pending':
    default:
      return <span aria-label="Pending" style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: 'var(--color-fg-muted)' }} />;
  }
}

function ValidationErrorList({ errors }: { errors: LocationImportValidationError[] }) {
  const { t } = useTranslation('common');
  return (
    <div role="alert" className="error">
      <p style={{ marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Warning size={16} aria-hidden />
        {t('locations.import.error.validation_failed', { count: errors.length })}
      </p>
      <ul style={{ marginLeft: '1rem', listStyle: 'disc' }}>
        {errors.slice(0, 8).map((e, i) => (
          <li key={i}>
            {t('locations.import.error.rowDetail', {
              row: e.row_index + 1,
              detail: t(`locations.import.error.reason.${e.reason}`, {
                field: t(`locations.import.field.${e.field}`, { defaultValue: e.field }),
                defaultValue: `${e.field} ${e.reason}`,
              }),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function replaceAt<T>(arr: T[], idx: number, value: T): T[] {
  const out = arr.slice();
  out[idx] = value;
  return out;
}

// resolveAll iterates pending rows with a small concurrency window and
// returns the final arrays in one batch. Per row:
//   1. Skip if no rawAddress (mark 'manual' — no Places to call).
//   2. Autocomplete with kinds='any' to include POI-style hits (sports halls,
//      stadiums) alongside street addresses. Take the top prediction.
//   3. PlaceDetails using the same session token so Google bills the pair as
//      one billable session.
//   4. On any error along the way, mark the row 'not_found' and let the user
//      either fix the address externally or click "use as-is".
async function resolveAll({
  token,
  language,
  region,
  rows,
  status,
}: {
  token: string | null;
  language: string;
  region: string;
  rows: ResolvedLocationRow[];
  status: ResolutionStatus[];
}): Promise<{ rows: ResolvedLocationRow[]; status: ResolutionStatus[] }> {
  const outRows = rows.slice();
  const outStatus = status.slice();

  const queue: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (status[i] === 'pending') queue.push(i);
  }

  const worker = async () => {
    for (;;) {
      const idx = queue.shift();
      if (idx === undefined) return;
      const row = outRows[idx];
      if (!row) continue;

      if (!row.rawAddress) {
        outStatus[idx] = 'manual';
        continue;
      }

      try {
        const session = newGeocodeSessionToken();
        const predictions = await geocodeAutocomplete(
          token,
          row.rawAddress,
          session,
          language,
          region,
          'any',
        );
        if (predictions.length === 0) {
          outStatus[idx] = 'not_found';
          continue;
        }
        const top = predictions[0]!;
        const details = await geocodePlaceDetails(token, top.place_id, session, language);
        outRows[idx] = mergePlace(row, details);
        outStatus[idx] = 'resolved';
      } catch (err) {
        clientLog.warn(`locations: geocode row ${idx} failed`, {
          error: err,
          fields: {
            step: 'locations/import',
            stage: 'geocode',
            rowIndex: idx,
          },
        });
        outStatus[idx] = 'not_found';
      }
    }
  };

  const workerCount = Math.min(GEOCODE_CONCURRENCY, queue.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return { rows: outRows, status: outStatus };
}

function mergePlace(row: ResolvedLocationRow, place: GeocodePlace): ResolvedLocationRow {
  return {
    ...row,
    place_id: place.place_id,
    formatted_address: place.formatted_address,
    street: place.street,
    postal_code: place.postal_code,
    city: place.city,
    country_code: place.country_code,
    latitude: place.latitude,
    longitude: place.longitude,
  };
}
